import { useState, useEffect, useCallback } from 'react';

import useGetAllTrapsActivity from './useGetAllTrapsActivity';

import { Period } from 'components/AnalyticsPage/Periods';
import { TrapType } from 'components/AnalyticsPage/TrapType';

interface ChartData {
  amount: number[];
  serial_number: string[];
  caught_empty_amount?: number[];
}

interface UseChartDataProps {
  slug: string;
  trapType: TrapType;
}

export const useAllTrapsActivityBarChartState = ({ slug, trapType }: UseChartDataProps) => {
  const [period, setPeriod] = useState<Period>(Period.THREE_MONTH);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showCustom, setShowCustom] = useState<boolean>(true);

  const { data, refetch, isLoading, isFetching } = useGetAllTrapsActivity(
    slug,
    period,
    startDate,
    endDate,
    trapType,
    false,
  );

  const [chartData, setChartData] = useState<ChartData>({
    amount: [],
    caught_empty_amount: [],
    serial_number: [],
  });

  useEffect(() => {
    if (!data || isFetching) {
      setChartData({ amount: [], serial_number: [], caught_empty_amount: [] });
      return;
    }

    const filteredData = data.filter((item: any) => item.serial_number !== null && item.serial_number !== undefined);

    const amount = data!.map((item: any) => item.amount);
    const serial_number = filteredData.map((item: any) => item.serial_number);
    const caught_empty_amount = data!.map((item: any) => item.caught_empty_amount || 0);
    setChartData({ amount, serial_number, caught_empty_amount });
  }, [data, trapType, isFetching]);

  useEffect(() => {
    const currentPath = window.location.pathname;

    if (!currentPath.includes('analitics')) {
      return;
    }

    if (startDate && endDate) {
      const timer = setTimeout(() => {
        refetch();
      }, 1000);

      return () => clearTimeout(timer);
    } else if (period !== 'CUSTOM') {
      refetch();
    }
  }, [slug, trapType, startDate, endDate, period, refetch]);

  const handlePeriodChange = useCallback((newPeriod: Period) => {
    if (newPeriod === 'CUSTOM') {
      const today = new Date();
      const oneYearBefore = new Date();
      oneYearBefore.setFullYear(today.getFullYear() - 1);

      setStartDate(oneYearBefore.toISOString().split('T')[0]);
      setEndDate(today.toISOString().split('T')[0]);
      setShowCustom(false);
    } else {
      setStartDate('');
      setEndDate('');
      setShowCustom(true);
    }
    setPeriod(newPeriod);
  }, []);

  return {
    period,
    startDate,
    endDate,
    showCustom,
    chartData,
    isLoading,
    isFetching,
    handlePeriodChange,
    setStartDate,
    setEndDate,
    refetch,
  };
};
